import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

export default function StaffManagementPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", salary: "", jobTitle: "", accountName: "", accountNumber: "", bankCode: "", bankName: "", frequency: "monthly" });

  const load = () => {
    apiClient.get(`/mtrade/companies/${companyId}/staff`)
      .then(r => { if (r.data.status) setStaff(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [companyId]);

  const addStaff = async () => {
    const { fullName, salary, accountName, accountNumber, bankCode } = form;
    if (!fullName || !salary || !accountName || !accountNumber || !bankCode) {
      return toast.error("Name, salary, account name, number and bank code are required");
    }
    try {
      const r = await apiClient.post(`/mtrade/companies/${companyId}/staff`, { ...form, salary: Number(form.salary) });
      if (r.data.status) { toast.success("Staff added"); setShowAdd(false); setForm({ fullName: "", email: "", salary: "", jobTitle: "", accountName: "", accountNumber: "", bankCode: "", bankName: "", frequency: "monthly" }); load(); }
      else toast.error(r.data.message);
    } catch (err) { toast.error(err?.response?.data?.message || "Failed"); }
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = text.split("\n").map(r => r.trim()).filter(Boolean);
    const header = rows.shift().split(",").map(h => h.trim());
    const parsed = rows.map(line => {
      const cells = line.split(",");
      const obj = {};
      header.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
      return obj;
    });
    try {
      const r = await apiClient.post(`/mtrade/companies/${companyId}/staff/bulk`, { staff: parsed });
      if (r.data.status) { toast.success(`${r.data.added} staff added`); load(); }
      else toast.error(r.data.message);
    } catch (err) { toast.error(err?.response?.data?.message || "Bulk upload failed"); }
  };

  const offboard = async (id) => {
    if (!confirm("Offboard this staff member?")) return;
    try { await apiClient.delete(`/mtrade/companies/${companyId}/staff/${id}`); toast.success("Offboarded"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <h1 className="text-primary font-bold text-lg font-display">Staff</h1>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold px-3 py-2 rounded-xl bg-surface-hover text-secondary cursor-pointer">
              CSV<input type="file" accept=".csv" onChange={handleCsv} className="hidden" />
            </label>
            <button onClick={() => setShowAdd(v => !v)} className="text-xs font-bold px-3 py-2 rounded-xl bg-brand text-on-brand">+ Add</button>
          </div>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-3xl mx-auto space-y-3">
        {showAdd && (
          <div className="bg-surface border border-border rounded-2xl p-4 space-y-2">
            {[["fullName", "Full name"], ["email", "Email (optional)"], ["jobTitle", "Job title"], ["salary", "Salary (₦)"], ["accountName", "Account name"], ["accountNumber", "Account number"], ["bankCode", "Bank code"], ["bankName", "Bank name"]].map(([k, label]) => (
              <input key={k} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                placeholder={label} type={k === "salary" ? "number" : "text"}
                className="w-full bg-surface-muted border border-border-strong rounded-xl px-3 py-2.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint" />
            ))}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl bg-surface-hover text-secondary text-sm font-semibold">Cancel</button>
              <button onClick={addStaff} className="flex-1 py-2.5 rounded-xl bg-brand text-on-brand text-sm font-bold">Save</button>
            </div>
            <p className="text-faint text-[11px] pt-1">CSV columns: fullName, email, salary, jobTitle, accountName, accountNumber, bankCode, bankName</p>
          </div>
        )}

        {loading ? (
          [1, 2, 3].map(i => <div key={i} className="h-16 bg-skeleton rounded-2xl animate-pulse" />)
        ) : staff.length === 0 ? (
          <p className="text-center text-muted text-sm py-10">No staff yet. Add individually or upload a CSV.</p>
        ) : (
          staff.map(s => (
            <div key={s._id} className="bg-surface border border-border rounded-2xl p-4 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-primary font-semibold text-sm truncate">{s.fullName}</p>
                <p className="text-muted text-xs">{s.jobTitle || "—"} · {s.bankName} {s.accountNumber}</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-primary font-bold text-sm">₦{Number(s.salary).toLocaleString()}</span>
                <button onClick={() => offboard(s._id)} className="w-8 h-8 rounded-lg bg-danger-light flex items-center justify-center">
                  <i className="ri-user-unfollow-line text-sm" style={{ color: "var(--color-danger)" }} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
